import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import SearchIcon from '@mui/icons-material/Search';
import { useProjects } from '../../context/ProjectContext';
import { changeRequestsApi } from '../../api/acceptanceApi';
import { featureApi, phaseApi } from '../../api/endpoints';
import { getApiErrorMessage } from '../../api/client';
import { CHANGE_REQUEST_STATUS_META } from './statusMeta';
import { ChangeRequestDetail } from './ChangeRequestDetail';
import { formatDate } from '../../utils/date';
import type { ChangeRequest, ChangeRequestStatus, Phase } from '../../types';

type StatusFilter = 'ALL' | ChangeRequestStatus;

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'ALL', label: 'Tất cả trạng thái' },
  { value: 'OPEN', label: 'Đang mở' },
  { value: 'IN_PROGRESS', label: 'Đang xử lý' },
  { value: 'RESOLVED', label: 'Đã xử lý' },
  { value: 'REJECTED', label: 'Từ chối' },
];

/** Danh sách + bộ lọc Change Request, dùng bởi trang /change-requests. */
export function ChangeRequestList(): JSX.Element {
  const { projects, selectedProjectId, setSelectedProjectId } = useProjects();

  const [phases, setPhases] = useState<Phase[]>([]);
  const [phaseFilter, setPhaseFilter] = useState<string>('ALL');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [search, setSearch] = useState('');

  const [items, setItems] = useState<ChangeRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [featureNames, setFeatureNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { id: ticketParam } = useParams<{ id?: string }>();

  useEffect(() => {
    setPage(0);
  }, [selectedProjectId, phaseFilter, statusFilter]);

  useEffect(() => {
    setPhaseFilter('ALL');
    if (!selectedProjectId) {
      setPhases([]);
      return;
    }
    let active = true;
    phaseApi
      .listByProject(selectedProjectId, { limit: 200 })
      .then((page) => {
        if (active) setPhases(page.items);
      })
      .catch(() => {
        if (active) setPhases([]);
      });
    return () => {
      active = false;
    };
  }, [selectedProjectId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await changeRequestsApi.list({
        project_id: selectedProjectId ?? undefined,
        phase_id: phaseFilter !== 'ALL' ? phaseFilter : undefined,
        status: statusFilter !== 'ALL' ? statusFilter : undefined,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      });
      const list = result.items;
      setItems(list);
      setTotal(result.total);

      const uniqueFeatureIds = Array.from(new Set(list.map((cr) => cr.feature_id)));
      const missing = uniqueFeatureIds.filter((id) => !(id in featureNames));
      if (missing.length > 0) {
        const fetched = await Promise.all(
          missing.map(async (fid) => {
            try {
              const f = await featureApi.get(fid);
              return [fid, f.name] as const;
            } catch {
              return [fid, 'Tính năng không xác định'] as const;
            }
          }),
        );
        setFeatureNames((prev) => ({ ...prev, ...Object.fromEntries(fetched) }));
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Không thể tải danh sách yêu cầu thay đổi.'));
      setItems([]);
    } finally {
      setLoading(false);
    }
    // featureNames intentionally excluded — chỉ dùng để tránh gọi lại API đã có.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId, phaseFilter, statusFilter, page, rowsPerPage]);

  useEffect(() => {
    void load();
  }, [load]);

  // Đồng bộ dự án đang chọn (ô Project trên AppBar) theo CR vừa mở qua
  // deep-link — tránh cảnh dialog hiện đúng CR nhưng danh sách phía sau vẫn
  // lọc theo dự án khác (hoặc rỗng) do chưa khớp project_id. Chỉ cần lấy
  // project_id nên gọi API riêng (nhẹ), không chờ ChangeRequestDetail tự fetch.
  useEffect(() => {
    if (!ticketParam) return;
    let active = true;
    changeRequestsApi
      .get(ticketParam)
      .then((detail) => {
        if (active && detail.request.project_id !== selectedProjectId) {
          setSelectedProjectId(detail.request.project_id);
        }
      })
      .catch(() => {
        // Lỗi (mã không tồn tại) đã được ChangeRequestDetail tự hiện Alert —
        // không cần xử lý trùng ở đây.
      });
    return () => {
      active = false;
    };
    // selectedProjectId cố ý không nằm trong dependency — chỉ đồng bộ MỘT LẦN
    // khi mở theo ticketParam, không chạy lại mỗi khi người dùng tự đổi ô chọn
    // dự án trong lúc dialog đang mở.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketParam, setSelectedProjectId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((cr) => {
      const featureName = featureNames[cr.feature_id] ?? '';
      return (
        cr.title.toLowerCase().includes(q) ||
        (cr.description ?? '').toLowerCase().includes(q) ||
        featureName.toLowerCase().includes(q) ||
        (cr.ticket_code ?? '').toLowerCase().includes(q)
      );
    });
  }, [items, search, featureNames]);

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 2.5 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="cr-project-label">Dự án</InputLabel>
              <Select
                labelId="cr-project-label"
                label="Dự án"
                value={selectedProjectId ?? ''}
                onChange={(e) => setSelectedProjectId(e.target.value || null)}
              >
                {projects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="cr-phase-label">Giai đoạn</InputLabel>
              <Select
                labelId="cr-phase-label"
                label="Giai đoạn"
                value={phaseFilter}
                onChange={(e) => setPhaseFilter(e.target.value)}
              >
                <MenuItem value="ALL">Tất cả giai đoạn</MenuItem>
                {phases.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="cr-status-label">Trạng thái</InputLabel>
              <Select
                labelId="cr-status-label"
                label="Trạng thái"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Tìm theo mã, tiêu đề, tính năng…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Mã</TableCell>
                <TableCell>Tính năng</TableCell>
                <TableCell>Tiêu đề</TableCell>
                <TableCell>Người tạo</TableCell>
                <TableCell>Trạng thái</TableCell>
                <TableCell>Ngày tạo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Alert severity="error">{error}</Alert>
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Không có yêu cầu thay đổi nào.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((cr) => {
                  const meta = CHANGE_REQUEST_STATUS_META[cr.status];
                  return (
                    <TableRow
                      key={cr.id}
                      hover
                      onClick={() => navigate(`/change-requests/${cr.ticket_code ?? cr.id}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {cr.ticket_code ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>{featureNames[cr.feature_id] ?? '…'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {cr.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {cr.creator_name ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={meta.label}
                          color={meta.color === 'default' ? 'default' : meta.color}
                          size="small"
                          variant={meta.color === 'default' ? 'outlined' : 'filled'}
                        />
                      </TableCell>
                      <TableCell>{formatDate(cr.created_at)}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number(e.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Số dòng/trang"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} / ${count}`}
        />
      </Paper>

      {ticketParam && (
        <ChangeRequestDetail
          changeRequestId={ticketParam}
          onClose={() => navigate('/change-requests')}
          onUpdated={() => void load()}
        />
      )}
    </Box>
  );
}
